(() => {
  const rootPath = window.Shopify?.routes?.root || '/';

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  };

  const getCartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

  const dispatchThemeCartUpdate = (sourceElement, payload = {}) => {
    const target = sourceElement || document.documentElement;

    target.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'shop-switcher',
          data: payload
        }
      })
    );
  };

  const openCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer-component');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }

    return false;
  };

  const buildCurrencyFormatter = (currencyCode, locale, showCurrencyCode) => {
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode || 'USD',
        currencyDisplay: showCurrencyCode ? 'code' : 'symbol'
      });
    } catch (_error) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const initShopSwitcher = (section) => {
    if (!section || section.dataset.shopSwitcherReady === 'true') return;
    section.dataset.shopSwitcherReady = 'true';

    const payload = section.querySelector('[data-shop-product-data]');
    const isAnimationTemplate = section.classList.contains('shop-switcher--animation');
    if (!payload) return;

    let payloadData;
    try {
      payloadData = JSON.parse(payload.textContent);
    } catch (_error) {
      return;
    }

    const products = payloadData.products || {};
    const labels = payloadData.labels || {};
    const subscriptionConfig = payloadData.subscription || {};
    const cansPerBox = Number(payloadData.cans_per_box || section.dataset.cansPerBox || 8);
    const shippingPrice = Number(payloadData.shipping_price || 0);
    const freeShippingBoxThreshold = Number(payloadData.free_shipping_box_threshold || 0);
    const manualShippingVariantId = Number(payloadData.manual_shipping_variant_id || 0);
    const currency = buildCurrencyFormatter(
      payloadData.currency_code,
      payloadData.currency_locale,
      Boolean(payloadData.show_currency_code)
    );
    const productCombinationCache = {};

    const tabButtons = section.querySelectorAll('[data-shop-product-tab]');
    const checkoutButton = section.querySelector('[data-shop-checkout]');
    const checkoutLabel = section.querySelector('[data-shop-checkout-label]');
    const optionsContainer = section.querySelector('[data-shop-options]');
    const galleryNav = section.querySelector('[data-shop-gallery-nav]');

    const productTitle = section.querySelector('[data-shop-product-title]');
    const productTitleMobile = section.querySelector('[data-shop-product-title-mobile]');
    const desktopDescriptionTitle = section.querySelector('[data-shop-description-title]');
    const desktopDescriptionBody = section.querySelector('[data-shop-description-body]');
    const mobileDescriptionTitle = section.querySelector('[data-shop-description-title-mobile]');
    const mobileDescriptionBody = section.querySelector('[data-shop-description-body-mobile]');
    const mainImage = section.querySelector('[data-shop-main-image]');
    const mainMedia = mainImage?.closest('.shop-switcher__main-media') || null;
    const sumItems = section.querySelectorAll('[data-shop-summary-items]');
    const sumShipping = section.querySelectorAll('[data-shop-summary-shipping]');
    const sumSubtotal = section.querySelectorAll('[data-shop-summary-subtotal]');
    const sumTotal = section.querySelectorAll('[data-shop-summary-total]');

    const subscriptionMinimumBoxes = Math.max(2, Number(subscriptionConfig.default_boxes || 2));

    let currentHandle = section.dataset.initialHandle;
    let currentMode = 'one-time';
    let currentOneTimeBoxes = Number(section.dataset.initialBoxes || 1);
    let currentBulkBoxes = 3;
    let currentSubscriptionBoxes = subscriptionMinimumBoxes;
    let currentGalleryIndex = 0;

    if (!products[currentHandle]) {
      currentHandle = Object.keys(products)[0];
    }

    Object.values(products).forEach((product) => {
      if (!product) return;

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const sellingPlans = Array.isArray(product.selling_plans) ? product.selling_plans : [];

      product.variants = variants
        .map((variant) => ({
          ...variant,
          box_count: Number(variant.box_count || 0),
          price: Number(variant.price || 0),
          available: variant.available !== false
        }))
        .filter((variant) => variant.box_count > 0)
        .sort((left, right) => left.box_count - right.box_count);

      product.selling_plans = sellingPlans
        .map((plan) => ({
          ...plan,
          id: Number(plan.id || 0)
        }))
        .filter((plan) => plan.id > 0);
    });

    const formatMoney = (value) => currency.format(Number(value || 0));
    const excludeShippingLabel = String(labels.exclude_shipping || '+ Excludes Shipping').trim() || '+ Excludes Shipping';
    const freeShippingLabel = String(labels.free_shipping || 'Free shipping').trim() || 'Free shipping';
    const freeSummaryLabel = String(labels.free_summary || 'Free').trim() || 'Free';
    const oneTimeSummaryLabel = String(labels.one_time_summary || 'One Time').trim() || 'One Time';
    const subscriptionSummaryLabel =
      String(labels.subscription_summary || 'Every Two Weeks').trim() || 'Every Two Weeks';
    const subscriptionDeliveryLabel =
      String(labels.subscription_delivery || 'delivered every 2 weeks').trim() || 'delivered every 2 weeks';
    const freeShippingThreshold = freeShippingBoxThreshold > 1 ? freeShippingBoxThreshold : 2;
    const getShippingForBoxCount = (boxCount) =>
      Number(boxCount || 0) >= freeShippingThreshold ? 0 : shippingPrice;

    const clampBoxes = (value, fallbackValue = 1, minimum = 1) =>
      Math.max(minimum, Number(value || fallbackValue || minimum));
    const clampSubscriptionBoxes = (value, fallbackValue = subscriptionMinimumBoxes) =>
      clampBoxes(value, fallbackValue, subscriptionMinimumBoxes);

    const getProductVariants = (product) => (Array.isArray(product?.variants) ? product.variants : []);

    const getBaseVariant = (product) => {
      const variants = getProductVariants(product);
      const preferredVariantId = Number(product?.variant_id || 0);

      return (
        variants.find((variant) => Number(variant.id) === preferredVariantId) ||
        variants.find((variant) => variant.box_count === 1) ||
        variants.find((variant) => variant.available !== false) ||
        variants[0] ||
        null
      );
    };

    const getSubscriptionPlan = (product) =>
      (Array.isArray(product?.selling_plans) ? product.selling_plans : []).find((plan) => Number(plan.id) > 0) || null;

    const getSubscriptionTotal = (product, boxCount) => {
      const planPrice = Number(getSubscriptionPlan(product)?.price || 0);
      return planPrice > 0
        ? planPrice * Math.max(1, Number(boxCount || 1))
        : getCombinationTotal(product, boxCount);
    };

    const formatBoxWord = (boxCount) => (Number(boxCount || 0) === 1 ? 'Box' : 'Boxes');

    const formatBoxLabel = (boxCount) => {
      const totalBoxes = Math.max(1, Number(boxCount || 1));
      return `${totalBoxes} ${formatBoxWord(totalBoxes)} (${totalBoxes * cansPerBox} cans)`;
    };

    const formatCansSummaryLabel = (boxCount, cadenceLabel) =>
      `${Math.max(1, Number(boxCount || 1)) * cansPerBox} cans, ${cadenceLabel}`;

    const getVariantCombination = (product, boxCount) => {
      const targetBoxes = Math.max(1, Number(boxCount || 1));
      const cacheKey = `${product?.handle || 'product'}:${targetBoxes}`;

      if (productCombinationCache[cacheKey]) {
        return productCombinationCache[cacheKey];
      }

      const baseVariant = getBaseVariant(product);
      if (!baseVariant || baseVariant.available === false) {
        productCombinationCache[cacheKey] = [];
        return [];
      }

      // Add the largest matching size first (6, 4, 2, 1 boxes) so the order reads "2 Boxes (16 Cans)" instead of "1 Box x 2".
      const sizedVariants = getProductVariants(product)
        .filter((variant) => variant.available !== false && Number(variant.box_count) > 0)
        .sort((left, right) => Number(right.box_count) - Number(left.box_count));

      const combination = [];
      let remainingBoxes = targetBoxes;

      sizedVariants.forEach((variant) => {
        const unitBoxes = Number(variant.box_count);
        const unitCount = Math.floor(remainingBoxes / unitBoxes);

        if (unitCount > 0) {
          combination.push({ variant, quantity: unitCount, unitBoxes });
          remainingBoxes -= unitCount * unitBoxes;
        }
      });

      if (remainingBoxes > 0 || combination.length === 0) {
        productCombinationCache[cacheKey] = [{ variant: baseVariant, quantity: targetBoxes, unitBoxes: 1 }];
        return productCombinationCache[cacheKey];
      }

      productCombinationCache[cacheKey] = combination;
      return combination;
    };

    const getCombinationTotal = (product, boxCount) =>
      getVariantCombination(product, boxCount).reduce(
        (sum, item) => sum + Number(item.variant.price || 0) * Number(item.quantity || 0),
        0
      );

    const getCurrentImages = () => {
      const product = products[currentHandle];
      return Array.isArray(product?.images) ? product.images : [];
    };

    const getCurrentBoxCount = () =>
      currentMode === 'subscription'
        ? clampSubscriptionBoxes(currentSubscriptionBoxes)
        : currentOneTimeBoxes >= 3
          ? clampBoxes(currentBulkBoxes, currentBulkBoxes, 3)
          : clampBoxes(currentOneTimeBoxes);

    const getAnimationPreviewImage = (product) => {
      if (!isAnimationTemplate || !product) return null;

      const previewImages = product.preview_images || {};
      const previewImage = previewImages.single;

      if (previewImage?.src) return previewImage;

      const fallbackImage = Array.isArray(product.images) ? product.images[0] : null;
      return fallbackImage?.src ? fallbackImage : null;
    };

    const equalizeAnimationOptionHeights = () => {
      if (!isAnimationTemplate || !optionsContainer) return;

      const optionCards = Array.from(optionsContainer.querySelectorAll('.shop-switcher__option--top'));
      if (!optionCards.length) return;

      let maxHeight = 0;

      optionCards.forEach((card) => {
        card.style.height = 'auto';
      });

      optionCards.forEach((card) => {
        if (card.offsetHeight > maxHeight) {
          maxHeight = card.offsetHeight;
        }
      });

      if (!maxHeight) return;

      optionCards.forEach((card) => {
        card.style.height = `${maxHeight}px`;
      });
    };

    const setGalleryIndex = (nextIndex) => {
      const images = getCurrentImages();
      if (!images.length) {
        currentGalleryIndex = 0;
        return;
      }

      const lastIndex = images.length - 1;
      currentGalleryIndex = Math.min(Math.max(nextIndex, 0), lastIndex);
      render();
    };

    const getOneTimeOptionMode = () => {
      if (currentOneTimeBoxes <= 1) return 'single';
      if (currentOneTimeBoxes === 2) return 'double';
      return 'bulk';
    };

    const setActiveTabs = (product) => {
      tabButtons.forEach((button) => {
        const active = button.dataset.shopProductTab === product.handle;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const getSelectionShipping = (selectionMode, boxCount) =>
      selectionMode === 'subscription' ? 0 : getShippingForBoxCount(boxCount);

    const getCustomShopProperties = (product, selectionMode, unitBoxes, sellingPlan) => ({
      _custom_shop: 'true',
      _custom_shop_handle: String(product?.handle || ''),
      // Boxes in one unit of this variant (1, 2, 4 or 6). A cart line's boxes are this times its quantity.
      _custom_shop_boxes: String(unitBoxes),
      _custom_shop_variant_label: formatBoxLabel(unitBoxes),
      // Per-box value (not the selection's) so the same product adds to the same cart line whatever the box count; free shipping at the threshold is decided from the cart's total boxes.
      _custom_shop_shipping_cents: String(Math.round(getSelectionShipping(selectionMode, 1) * 100)),
      _custom_shop_free_shipping_threshold: String(freeShippingThreshold),
      _custom_shop_purchase_type: selectionMode === 'subscription' ? 'subscription' : 'one-time',
      _custom_shop_purchase_cadence:
        selectionMode === 'subscription' ? subscriptionSummaryLabel : oneTimeSummaryLabel,
      ...(sellingPlan?.name ? { _custom_shop_selling_plan: String(sellingPlan.name) } : {})
    });

    const getCartItems = (product) => {
      const selectionMode = currentMode;
      const selectedBoxes = getCurrentBoxCount();
      const combination = getVariantCombination(product, selectedBoxes);
      const sellingPlan = selectionMode === 'subscription' ? getSubscriptionPlan(product) : null;

      if (!combination.length) return [];

      const cartItems = combination.map((item) => {
        const cartItem = {
          id: Number(item.variant.id),
          quantity: Number(item.quantity || 0),
          properties: getCustomShopProperties(product, selectionMode, Number(item.unitBoxes || 1), sellingPlan)
        };

        if (sellingPlan?.id) {
          cartItem.selling_plan = Number(sellingPlan.id);
        }

        return cartItem;
      });

      if (selectionMode === 'one-time' && selectedBoxes === 1 && manualShippingVariantId > 0) {
        cartItems.push({
          id: manualShippingVariantId,
          quantity: 1,
          properties: {
            _custom_shop: 'true',
            _custom_shop_fee: 'shipping'
          }
        });
      }

      return cartItems;
    };

    const getPricing = (product) => {
      const selectionMode = currentMode;
      const selectedBoxes = getCurrentBoxCount();
      const subtotal =
        selectionMode === 'subscription'
          ? getSubscriptionTotal(product, selectedBoxes)
          : getCombinationTotal(product, selectedBoxes);
      const shipping = getSelectionShipping(selectionMode, selectedBoxes);
      const cartItems = getCartItems(product);

      return {
        subtotal,
        shipping,
        total: subtotal + shipping,
        available: cartItems.length > 0
      };
    };

    const renderGallery = (product) => {
      if (isAnimationTemplate) {
        const previewImage = getAnimationPreviewImage(product);

        if (mainImage && previewImage) {
          mainImage.src = previewImage.src;
          mainImage.alt = previewImage.alt || product.display_heading || product.heading || '';
        }

        if (galleryNav) {
          galleryNav.innerHTML = '';
        }

        return;
      }

      const images = Array.isArray(product.images) && product.images.length ? product.images : [];
      const currentImage = images[currentGalleryIndex] || images[0];

      if (mainImage && currentImage) {
        mainImage.src = currentImage.src;
        mainImage.alt = currentImage.alt || product.display_heading || product.heading || '';
      }

      if (!galleryNav) return;

      galleryNav.innerHTML = images
        .map(
          (image, index) => `
            <button
              class="shop-switcher__gallery-thumb${index === currentGalleryIndex ? ' is-active' : ''}"
              type="button"
              data-shop-gallery-index="${index}"
              aria-label="View image ${index + 1}"
            >
              <img src="${escapeHtml(image.thumb || image.src)}" alt="${escapeHtml(image.alt || product.heading || '')}" loading="lazy">
            </button>
          `
        )
        .join('');
    };

    const renderOptionNote = (label, modifier = '', showIcon = false) => `
      <span class="shop-switcher__option-note${modifier ? ` ${modifier}` : ''}">
        ${
          showIcon
            ? `<span class="shop-switcher__option-note-icon" aria-hidden="true">
                 <svg viewBox="0 0 12 12" focusable="false">
                   <path d="M4.79 8.69 2.2 6.1l-.88.88 3.47 3.47 6.2-6.2-.88-.88-5.32 5.32Z" fill="currentColor"></path>
                 </svg>
               </span>`
            : ''
        }
        <span class="shop-switcher__option-note-text">${escapeHtml(label)}</span>
      </span>
    `;

    const renderStepper = ({ value, changeAttr, valueAttr, sublabel = '', modifier = '', inlineLabel = false }) => `
      <span class="shop-switcher__option-stepper${modifier ? ` ${modifier}` : ''}">
        <button
          type="button"
          class="shop-switcher__option-stepper-button"
          ${changeAttr}="-1"
          aria-label="Decrease quantity"
        >
          -
        </button>
        <span class="shop-switcher__option-stepper-value"${valueAttr ? ` ${valueAttr}` : ''}>
          <span class="shop-switcher__option-stepper-number">${value}</span>
          ${
            sublabel
              ? `<span class="shop-switcher__option-stepper-sublabel${inlineLabel ? ' shop-switcher__option-stepper-sublabel--inline' : ''}">${escapeHtml(sublabel)}</span>`
              : ''
          }
        </span>
        <button
          type="button"
          class="shop-switcher__option-stepper-button"
          ${changeAttr}="1"
          aria-label="Increase quantity"
        >
          +
        </button>
      </span>
    `;

    const renderSubscriptionBenefit = (text) => `
      <span class="shop-switcher__subscription-benefit">
        <span class="shop-switcher__subscription-benefit-icon" aria-hidden="true">
          <svg viewBox="0 0 14 14" focusable="false">
            <circle cx="7" cy="7" r="6" fill="#FFF8E8" stroke="#CFA745" stroke-width="1.2"></circle>
            <path d="M5.86 9.2 4.13 7.47l-.66.66 2.39 2.4 4.67-4.67-.66-.66L5.86 9.2Z" fill="#173768"></path>
          </svg>
        </span>
        <span>${escapeHtml(text)}</span>
      </span>
    `;

    const renderOptions = (product) => {
      if (!optionsContainer) return;

      const oneTimeMode = getOneTimeOptionMode();
      const bulkBoxes = clampBoxes(currentBulkBoxes, currentBulkBoxes, 3);
      const subscriptionBoxes = clampSubscriptionBoxes(currentSubscriptionBoxes);
      const singlePrice = getCombinationTotal(product, 1);
      const doublePrice = getCombinationTotal(product, 2);
      const bulkPrice = getCombinationTotal(product, bulkBoxes);
      const subscriptionPrice = getSubscriptionTotal(product, subscriptionBoxes);
      const popularBadgeClasses = isAnimationTemplate
        ? 'shop-switcher__option-badge shop-switcher__option-badge--ribbon'
        : 'shop-switcher__option-badge';
      const subscriptionCadenceText = isAnimationTemplate
        ? String(subscriptionDeliveryLabel).replace(/\b2\b/g, 'two')
        : formatBoxLabel(subscriptionBoxes) + ' ' + subscriptionDeliveryLabel;
      const subscriptionPerDeliveryText = `${subscriptionBoxes * cansPerBox} cans`;

      optionsContainer.innerHTML = `
        <div class="shop-switcher__options-grid" role="radiogroup" aria-label="Choose product quantity or subscription">
          <div
            class="shop-switcher__option shop-switcher__option--top${currentMode === 'one-time' && oneTimeMode === 'single' ? ' is-active' : ''}"
            role="radio"
            aria-checked="${currentMode === 'one-time' && oneTimeMode === 'single' ? 'true' : 'false'}"
            tabindex="0"
            data-shop-option="single"
          >
            <span class="shop-switcher__option-radio"></span>
            <span class="shop-switcher__option-title">1 ${formatBoxWord(1)}</span>
            <span class="shop-switcher__option-cans-label">(${1 * cansPerBox} cans)</span>
            <span class="shop-switcher__option-detail shop-switcher__option-detail--price">
              <span class="shop-switcher__option-price">${formatMoney(singlePrice)}</span>
              ${renderOptionNote(excludeShippingLabel, 'shop-switcher__option-note--neutral')}
            </span>
          </div>

          <div
            class="shop-switcher__option shop-switcher__option--top is-popular${currentMode === 'one-time' && oneTimeMode === 'double' ? ' is-active' : ''}"
            role="radio"
            aria-checked="${currentMode === 'one-time' && oneTimeMode === 'double' ? 'true' : 'false'}"
            tabindex="0"
            data-shop-option="double"
          >
            <span class="${popularBadgeClasses}">${escapeHtml(labels.popular_badge || 'Most popular')}</span>
            <span class="shop-switcher__option-radio"></span>
            <span class="shop-switcher__option-title">2 ${formatBoxWord(2)}</span>
            <span class="shop-switcher__option-cans-label">(${2 * cansPerBox} cans)</span>
            <span class="shop-switcher__option-detail shop-switcher__option-detail--price">
              <span class="shop-switcher__option-price">${formatMoney(doublePrice)}</span>
              ${renderOptionNote(freeShippingLabel, 'shop-switcher__option-note--free', true)}
            </span>
          </div>

          <div
            class="shop-switcher__option shop-switcher__option--top shop-switcher__option--bulk${currentMode === 'one-time' && oneTimeMode === 'bulk' ? ' is-active' : ''}"
            role="radio"
            aria-checked="${currentMode === 'one-time' && oneTimeMode === 'bulk' ? 'true' : 'false'}"
            aria-label="3 or more boxes"
            tabindex="0"
            data-shop-option="bulk"
          >
            <span class="shop-switcher__option-radio"></span>
            <span class="visually-hidden">3+ boxes</span>
            <div class="shop-switcher__option-bulk-stepper">
              ${renderStepper({
                value: bulkBoxes,
                changeAttr: 'data-shop-bulk-change',
                valueAttr: 'data-shop-bulk-value',
                sublabel: formatBoxWord(bulkBoxes),
                modifier: 'shop-switcher__option-stepper--bulk shop-switcher__option-stepper--inline',
                inlineLabel: true
              })}
            </div>
            <span class="shop-switcher__option-cans-label">(${bulkBoxes * cansPerBox} cans)</span>
            <span class="shop-switcher__option-detail shop-switcher__option-detail--price">
              <span class="shop-switcher__option-price">${formatMoney(bulkPrice)}</span>
              ${renderOptionNote(freeShippingLabel, 'shop-switcher__option-note--free', true)}
            </span>
          </div>

          <div
            class="shop-switcher__subscription${currentMode === 'subscription' ? ' is-active' : ''}"
            role="radio"
            aria-checked="${currentMode === 'subscription' ? 'true' : 'false'}"
            tabindex="0"
            data-shop-option="subscription"
          >
            <div class="shop-switcher__subscription-selector">
              <span class="shop-switcher__option-radio"></span>
              <span class="shop-switcher__subscription-title">${escapeHtml(labels.subscribe || 'Subscribe')}</span>
            </div>
            <div class="shop-switcher__subscription-stepper-wrap">
              ${renderStepper({
                value: subscriptionBoxes,
                changeAttr: 'data-shop-subscription-change',
                valueAttr: 'data-shop-subscription-value',
                sublabel: formatBoxWord(subscriptionBoxes),
                modifier: isAnimationTemplate
                  ? 'shop-switcher__option-stepper--bulk shop-switcher__option-stepper--inline shop-switcher__option-stepper--subscription'
                  : '',
                inlineLabel: isAnimationTemplate
              })}
            </div>
            <div class="shop-switcher__subscription-copy">
              <span class="shop-switcher__subscription-delivery">
                ${escapeHtml(subscriptionCadenceText)}
              </span>
              ${
                isAnimationTemplate
                  ? renderOptionNote(
                      labels.subscription_benefit_2 || 'Cancel Anytime',
                      'shop-switcher__option-note--free shop-switcher__subscription-note shop-switcher__subscription-note--selector',
                      true
                    )
                  : ''
              }
            </div>
            <div class="shop-switcher__subscription-price-block">
              <span class="shop-switcher__subscription-price">${formatMoney(subscriptionPrice)}</span>
              ${
                isAnimationTemplate
                  ? `
                    <span class="shop-switcher__subscription-price-suffix shop-switcher__subscription-price-suffix--delivery">
                      ${escapeHtml(subscriptionPerDeliveryText)}
                    </span>
                    ${renderOptionNote(
                      labels.subscription_benefit_1 || 'Fast Free Shipping',
                      'shop-switcher__option-note--free shop-switcher__subscription-note shop-switcher__subscription-note--price',
                      true
                    )}
                  `
                  : `
                    <span class="shop-switcher__subscription-price-suffix">
                      ${escapeHtml(labels.subscription_price_suffix || 'per delivery')}
                    </span>
                  `
              }
            </div>
            ${
              isAnimationTemplate
                ? ''
                : `
                  <div class="shop-switcher__subscription-benefits-column">
                    <span class="shop-switcher__subscription-benefits">
                      ${renderSubscriptionBenefit(labels.subscription_benefit_1 || 'Fast Free Shipping')}
                      ${renderSubscriptionBenefit(labels.subscription_benefit_2 || 'Cancel Anytime')}
                    </span>
                  </div>
                `
            }
          </div>
        </div>
      `;
    };

    const updateSummary = (_product, pricing) => {
      const selectedBoxes = getCurrentBoxCount();
      const cadenceLabel = currentMode === 'subscription' ? subscriptionSummaryLabel : oneTimeSummaryLabel;
      const itemsText = formatCansSummaryLabel(selectedBoxes, cadenceLabel);
      const subtotal = Number(pricing.subtotal || 0);
      const shipping = Number(pricing.shipping || 0);
      const total = Number(pricing.total || 0);

      sumItems.forEach((node) => {
        node.textContent = itemsText;
      });

      sumSubtotal.forEach((node) => {
        node.textContent = formatMoney(subtotal);
      });

      sumShipping.forEach((node) => {
        node.textContent = shipping > 0 ? formatMoney(shipping) : freeSummaryLabel;
        node.classList.toggle('is-free', shipping === 0);
      });

      sumTotal.forEach((node) => {
        node.textContent = formatMoney(total);
      });
    };

    const updateCopy = (product) => {
      const displayHeading = product.display_heading || product.heading;
      if (productTitle) productTitle.textContent = displayHeading;
      if (productTitleMobile) productTitleMobile.textContent = displayHeading;
      if (desktopDescriptionTitle) desktopDescriptionTitle.textContent = product.description_title;
      if (desktopDescriptionBody) desktopDescriptionBody.innerHTML = product.description_desktop;
      if (mobileDescriptionTitle) mobileDescriptionTitle.textContent = product.description_title;
      if (mobileDescriptionBody) mobileDescriptionBody.innerHTML = product.description_mobile;
    };

    const updateCheckout = (product, pricing) => {
      if (!checkoutButton) return;

      const cartItems = getCartItems(product);
      checkoutButton.dataset.cartItems = JSON.stringify(cartItems);
      checkoutButton.disabled = !cartItems.length || pricing.available === false;

      if (checkoutLabel) {
        checkoutLabel.textContent = checkoutButton.dataset.checkoutText || 'Add to cart';
      }
    };

    const render = () => {
      const product = products[currentHandle];
      if (!product) return;

      currentOneTimeBoxes = clampBoxes(currentOneTimeBoxes, product.default_boxes || 1);
      currentBulkBoxes = clampBoxes(currentBulkBoxes, currentBulkBoxes, 3);
      currentSubscriptionBoxes = clampSubscriptionBoxes(currentSubscriptionBoxes);

      if (!Array.isArray(product.images) || currentGalleryIndex >= product.images.length) {
        currentGalleryIndex = 0;
      }

      const pricing = getPricing(product);

      setActiveTabs(product);
      renderOptions(product);
      renderGallery(product);
      updateCopy(product);
      updateSummary(product, pricing);
      updateCheckout(product, pricing);
      equalizeAnimationOptionHeights();
    };

    tabButtons.forEach((button) => {
      button.addEventListener('click', () => {
        currentHandle = button.dataset.shopProductTab;
        currentOneTimeBoxes = clampBoxes(products[currentHandle]?.default_boxes || currentOneTimeBoxes);
        currentBulkBoxes = clampBoxes(currentBulkBoxes, currentBulkBoxes, 3);
        currentSubscriptionBoxes = clampSubscriptionBoxes(currentSubscriptionBoxes);
        currentGalleryIndex = 0;
        render();
      });
    });

    const activateOption = (optionKey) => {
      if (optionKey === 'single') {
        currentMode = 'one-time';
        currentOneTimeBoxes = 1;
      }

      if (optionKey === 'double') {
        currentMode = 'one-time';
        currentOneTimeBoxes = 2;
      }

      if (optionKey === 'bulk') {
        currentMode = 'one-time';
        currentBulkBoxes = clampBoxes(currentBulkBoxes, currentBulkBoxes, 3);
        currentOneTimeBoxes = currentBulkBoxes;
      }

      if (optionKey === 'subscription') {
        currentMode = 'subscription';
        currentSubscriptionBoxes = clampSubscriptionBoxes(currentSubscriptionBoxes);
      }

      render();
    };

    if (optionsContainer) {
      optionsContainer.addEventListener('click', (event) => {
        const option = event.target.closest('[data-shop-option]');
        const bulkChangeButton = event.target.closest('[data-shop-bulk-change]');
        const subscriptionChangeButton = event.target.closest('[data-shop-subscription-change]');

        if (bulkChangeButton) {
          event.preventDefault();
          event.stopPropagation();
          currentMode = 'one-time';
          currentBulkBoxes = clampBoxes(
            currentBulkBoxes + Number(bulkChangeButton.dataset.shopBulkChange || 0),
            currentBulkBoxes,
            3
          );
          currentOneTimeBoxes = currentBulkBoxes;
          render();
          return;
        }

        if (subscriptionChangeButton) {
          event.preventDefault();
          event.stopPropagation();
          currentMode = 'subscription';
          currentSubscriptionBoxes = clampSubscriptionBoxes(
            currentSubscriptionBoxes + Number(subscriptionChangeButton.dataset.shopSubscriptionChange || 0)
          );
          render();
          return;
        }

        if (!option) return;
        activateOption(option.dataset.shopOption);
      });

      optionsContainer.addEventListener('keydown', (event) => {
        const option = event.target.closest('[data-shop-option]');
        if (!option) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activateOption(option.dataset.shopOption);
      });
    }

    if (galleryNav) {
      galleryNav.addEventListener('click', (event) => {
        const button = event.target.closest('[data-shop-gallery-index]');
        if (!button) return;
        setGalleryIndex(Number(button.dataset.shopGalleryIndex || 0));
      });
    }

    if (mainMedia) {
      let touchStartX = 0;
      let touchStartY = 0;

      mainMedia.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches?.[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        },
        { passive: true }
      );

      mainMedia.addEventListener(
        'touchend',
        (event) => {
          const touch = event.changedTouches?.[0];
          const images = getCurrentImages();
          if (!touch || images.length < 2) return;

          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;

          if (Math.abs(deltaX) < 30 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

          if (deltaX < 0) {
            setGalleryIndex(currentGalleryIndex + 1);
          } else {
            setGalleryIndex(currentGalleryIndex - 1);
          }
        },
        { passive: true }
      );
    }

    if (checkoutButton) {
      checkoutButton.addEventListener('click', async () => {
        const cartItems = JSON.parse(checkoutButton.dataset.cartItems || '[]');

        if (!cartItems.length) return;

        checkoutButton.disabled = true;

        try {
          const sectionIds = getCartSectionIds();
          const itemCount = cartItems.reduce((count, item) => count + Number(item.quantity || 0), 0);
          const response = await fetchJson(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: cartItems,
              sections: sectionIds,
              sections_url: window.location.pathname
            })
          });

          dispatchThemeCartUpdate(section, {
            source: 'product-form-component',
            itemCount,
            sections: response.sections || {}
          });

          requestAnimationFrame(() => {
            if (!openCartDrawer()) {
              window.location.href = `${rootPath}cart`;
            }
          });
        } catch (_error) {
          window.location.href = `${rootPath}cart`;
        } finally {
          checkoutButton.disabled = false;
        }
      });
    }

    window.addEventListener('resize', equalizeAnimationOptionHeights);

    render();
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-shop-switcher-section]').forEach(initShopSwitcher);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
